import { ReactNode, useRef } from "react";

import {
  Box,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Heading,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react";

import { InfoIcon, TerminalIcon } from "@/components/shared/ReleaseIcons";
import { LearnMoreEntryKey, learnMore } from "@/constants/learnMore";
import { useRightDrawer } from "@/providers/useDrawers";
import { useTerminals } from "@/providers/useTerminals";

type LearnMoreSidebarSection = {
  heading: string;
  key?: string;
  paragraphs: {
    paragraph: string | ReactNode;
    key: string;
  }[];
};

export type LearnMoreSidebarEntry =
  | LearnMoreSidebarSection
  | Array<LearnMoreSidebarSection & { key: string }>;

interface LearnMoreSidebarProps {
  entryKeys: LearnMoreEntryKey[];
}

export default function LearnMoreSidebar({ entryKeys }: LearnMoreSidebarProps) {
  const { isOpen, onOpen, onClose, highlightBlockId } = useRightDrawer();
  const { terminalPanelCollapsed, expandTerminalPanel } = useTerminals();
  const highlightRef = useRef<null | HTMLDivElement>(null);
  const { panelTerminals } = useTerminals();

  highlightRef?.current?.scrollIntoView({ behavior: "smooth" });

  const renderEntry = (section: LearnMoreSidebarSection, key?: string) => {
    key = section.key || key;
    const selected = highlightBlockId === key;
    return (
      <VStack
        key={key}
        ref={selected ? highlightRef : null}
        align="stretch"
        layerStyle={selected ? "highlightBlock" : ""}
        p="4"
        spacing="6"
        w="full"
      >
        <Heading size="md">{section.heading}</Heading>
        {section.paragraphs.map(({ paragraph, key: paragraphKey }) =>
          typeof paragraph === "string" ? (
            <Text key={paragraphKey}>{paragraph}</Text>
          ) : (
            <Box key={paragraphKey}>{paragraph}</Box>
          )
        )}
      </VStack>
    );
  };

  return (
    <Flex
      align="center"
      borderLeft="1px"
      borderLeftColor="inherit"
      direction="column"
      h="full"
      justifyContent="space-between"
      w="full"
    >
      <Drawer isOpen={isOpen} placement="right" size="sm" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent
          sx={{
            h: "full",
            borderLeftWidth: "1px",
            lineHeight: "24px",
            p: 4,
            pt: 10,
          }}
        >
          <DrawerCloseButton />
          <VStack spacing="2">
            {entryKeys.map(entryKey => {
              const entry = learnMore[entryKey];
              return Array.isArray(entry)
                ? entry.map(section => renderEntry(section))
                : renderEntry(entry, entryKey);
            })}
          </VStack>
        </DrawerContent>
      </Drawer>
      <Box cursor="pointer" onClick={onOpen}>
        <VStack>
          <IconButton
            aria-label="help"
            colorScheme="niagara"
            icon={<InfoIcon />}
            mt="3"
            size="sm"
            variant="ghost"
          />
          <Text variant="verticalLabel">Learn more</Text>
        </VStack>
      </Box>
      <Box mb={2}>
        {panelTerminals.length > 0 && terminalPanelCollapsed && (
          <IconButton
            aria-label="terminal-panel"
            icon={<TerminalIcon />}
            size="lg"
            title="Expand Terminals"
            variant="highlight"
            onClick={expandTerminalPanel}
          />
        )}
      </Box>
    </Flex>
  );
}
